/**
 * The one money formatter. THE UNIT IS PENNIES IN.
 *
 * Every function here takes `cents`. There is no dollars-in counterpart and
 * there will not be one: the unit is the whole bug, and a second entry point
 * taking the other unit is how twelve call sites came to disagree about which
 * one they were passing. A caller holding dollars multiplies by 100 at the
 * point it holds them, where the unit is still obvious, not here.
 */
import { numberFormatter } from "./intl.js";
/**
 * Pennies as a whole number, rounded half away from zero to the precision that
 * will be printed.
 *
 * Rounding here rather than leaving it to `Intl` makes the result independent
 * of the engine's rounding mode, and away-from-zero is the money convention: a
 * refund of 1234.5 pennies and a charge of 1234.5 pennies are the same
 * magnitude, and a rule that rounds -1234.5 toward positive infinity makes them
 * differ by a cent. Rounding straight to the printed precision is what keeps
 * 1949.5 pennies at $19 when whole dollars are asked for: rounding to a penny
 * first and to a dollar second carries $19.495 up to $19.50 and then to $20.
 */
function toRoundedCents(cents, fractionDigits) {
    if (cents === null || cents === undefined)
        return 0;
    // `Number` reads the whole string or nothing. `Number.parseFloat` reads a
    // PREFIX, which silently reads the display value "1,999" as one cent and
    // "1999px" as $19.99. A value carrying a grouping separator or a currency
    // mark is formatted output rather than the penny count this takes, and which
    // number it names depends on a locale this has not been told ("1.999" is a
    // thousand in de-DE), so it is non-numeric here and formats as zero.
    const raw = typeof cents === "string" ? Number(cents) : cents;
    if (!Number.isFinite(raw))
        return 0;
    const step = fractionDigits === 0 ? 100 : 1;
    const whole = Math.sign(raw) * Math.round(Math.abs(raw) / step) * step;
    // `-0` renders as "-$0.00", which reads as a credit that is not there.
    return whole === 0 ? 0 : whole;
}
/**
 * An amount in pennies as a currency string: `formatCurrency(1999)` is
 * "$19.99".
 */
export function formatCurrency(cents, opts = {}) {
    const { fractionDigits = 2, currency = "USD", locale = "en-US" } = opts;
    return numberFormatter(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(toRoundedCents(cents, fractionDigits) / 100);
}
/**
 * An amount in pennies as a bare figure: `formatCurrencyNoSymbol(123456)` is
 * "1,234.56".
 *
 * For a surface that prints its own currency mark beside the figure — a column
 * header, a large stat tile — where `formatCurrency`'s symbol would be a second
 * one.
 */
export function formatCurrencyNoSymbol(cents, opts = {}) {
    const { fractionDigits = 2, locale = "en-US" } = opts;
    return numberFormatter(locale, {
        style: "decimal",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(toRoundedCents(cents, fractionDigits) / 100);
}
